/**
 * FieldCatalogSidebar.js
 *
 * Comprehensive field catalog sidebar for Phase 7 Part 2.
 * Shows all available fields organized by group, with filtering by template category.
 * Allows users to:
 * - Filter fields by template category
 * - Browse fields by group
 * - Drag fields to canvas or click to place
 * - Manage indexed families (owners, banks, entities, early pay)
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';
import { dragStateStore } from '../utils/dragStateStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function FieldCatalogSidebar({
  templateCategory,
  token,
  onFieldAdd,
}) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [filterText, setFilterText] = useState('');

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contract-templates/field-catalog`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCatalog(res.data);
      // All groups collapsed by default
      setExpandedGroups({});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load field catalog');
      console.error('Fetch catalog error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  if (loading) {
    return <div style={styles.container}>Loading field catalog...</div>;
  }

  if (error) {
    return <div style={styles.container}><div style={styles.error}>{error}</div></div>;
  }

  if (!catalog) {
    return <div style={styles.container}>No catalog data</div>;
  }

  // Group fields by group and filter by template category and search text
  const groupedFields = {};
  catalog.fields.forEach(field => {
    if (!templateCategory || field.template_categories.includes(templateCategory)) {
      if (!filterText || field.label.toLowerCase().includes(filterText.toLowerCase()) ||
          field.field_key.toLowerCase().includes(filterText.toLowerCase())) {
        const group = field.group;
        if (!groupedFields[group]) {
          groupedFields[group] = [];
        }
        groupedFields[group].push(field);
      }
    }
  });

  // Sort groups by the order in the field catalog groups definition
  const orderedGroups = Object.keys(catalog.groups).filter(g => groupedFields[g]);

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Fields</h3>

      <input
        type="text"
        placeholder="Search fields..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        style={styles.searchInput}
      />

      {templateCategory && (
        <div style={styles.categoryBadge}>
          Category: <strong>{catalog.template_categories[templateCategory] || templateCategory}</strong>
        </div>
      )}

      <div style={styles.fieldsList}>
        {orderedGroups.length === 0 ? (
          <p style={styles.emptyMessage}>No fields available for selected category</p>
        ) : (
          orderedGroups.map(groupKey => (
            <div key={groupKey} style={styles.group}>
              <button
                onClick={() => {
                  setExpandedGroups(prev => ({
                    ...prev,
                    [groupKey]: !prev[groupKey]
                  }));
                }}
                style={styles.groupHeader}
              >
                <span style={styles.groupToggle}>
                  {expandedGroups[groupKey] ? '▼' : '▶'}
                </span>
                <span style={styles.groupName}>
                  {catalog.groups[groupKey]}
                </span>
                <span style={styles.groupCount}>
                  {groupedFields[groupKey].length}
                </span>
              </button>

              {expandedGroups[groupKey] && (
                <div style={styles.groupFields}>
                  {groupedFields[groupKey].map(field => (
                    <div
                      key={field.field_key}
                      onMouseDown={(e) => {
                        // Prevent text selection when starting drag
                        e.preventDefault();

                        // Start custom drag: store field data in shared store
                        dragStateStore.field = {
                          field_key: field.field_key,
                          label: field.label,
                          render_type: field.render_type,
                          width: field.render_type === 'checkbox' ? 5 : 100,
                          height: field.render_type === 'checkbox' ? 10 : 20,
                        };
                        dragStateStore.isActive = true;
                        console.log('👆 [SIDEBAR DRAG START]', field.label);
                      }}
                      onMouseUp={() => {
                        // End custom drag
                        console.log('☝️  [SIDEBAR DRAG END] (released on sidebar)');
                        dragStateStore.isActive = false;
                        dragStateStore.field = null;
                      }}
                      onClick={() => onFieldAdd && onFieldAdd(field)}
                      style={{
                        ...styles.fieldItem,
                        cursor: 'grab',
                      }}
                      title={field.notes || field.label}
                    >
                      <div style={styles.fieldLabel}>{field.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div style={styles.footer}>
        <small style={{ color: COLORS.TEXT_LIGHT }}>
          Drag fields onto the PDF to place them
        </small>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRight: `1px solid ${COLORS.BORDER_LIGHT}`,
    overflow: 'hidden',
  },
  title: {
    ...TYPOGRAPHY.H3,
    padding: SPACING.LG,
    marginTop: 0,
    marginBottom: 0,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    color: COLORS.TEXT_PRIMARY,
  },
  searchInput: {
    margin: SPACING.MD,
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    backgroundColor: COLORS.BG_WHITE,
    color: COLORS.TEXT_PRIMARY,
    ...TYPOGRAPHY.BODY_SM,
  },
  categoryBadge: {
    margin: `0 ${SPACING.MD} ${SPACING.MD} ${SPACING.MD}`,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: COLORS.PRIMARY_LIGHT,
    color: COLORS.TEXT_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    ...TYPOGRAPHY.BODY_SM,
  },
  fieldsList: {
    flex: 1,
    overflow: 'auto',
    paddingBottom: SPACING.LG,
  },
  emptyMessage: {
    padding: SPACING.LG,
    color: COLORS.TEXT_LIGHT,
    textAlign: 'center',
    ...TYPOGRAPHY.BODY_SM,
  },
  group: {
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },
  groupHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: SPACING.MD,
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    transition: 'background-color 0.2s',
  },
  groupToggle: {
    marginRight: SPACING.SM,
    color: COLORS.TEXT_LIGHT,
    ...TYPOGRAPHY.BODY_SM,
  },
  groupName: {
    flex: 1,
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    ...TYPOGRAPHY.BODY,
  },
  groupCount: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
  },
  groupFields: {
    padding: SPACING.SM,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldItem: {
    padding: `${SPACING.SM} ${SPACING.MD}`,
    backgroundColor: 'rgba(77, 219, 148, 0.08)',
    border: `1px solid ${COLORS.PRIMARY}`,
    borderRadius: RADIUS.DEFAULT,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  fieldLabel: {
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    ...TYPOGRAPHY.BODY_SM,
    margin: 0,
  },
  fieldMeta: {
    display: 'flex',
    gap: SPACING.SM,
    alignItems: 'center',
  },
  renderType: {
    display: 'inline-block',
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_LIGHT,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    borderRadius: RADIUS.DEFAULT,
    ...TYPOGRAPHY.BODY_XS,
  },
  indexContext: {
    display: 'inline-block',
    backgroundColor: COLORS.PRIMARY_LIGHT,
    color: COLORS.TEXT_PRIMARY,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    borderRadius: RADIUS.DEFAULT,
    ...TYPOGRAPHY.BODY_XS,
    fontWeight: '600',
  },
  footer: {
    padding: SPACING.MD,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    textAlign: 'center',
  },
};
